import { useState, useMemo } from 'react';
import { actMathWorksheets } from '../worksheets/worksheets-act-math';

type WeekOption = 4 | 6 | 8 | 12;

interface ScheduledWorksheet {
  id: string;
  title: string;
  domain: string;
  pages: number;
  difficulty: string;
  estimatedTime: number;
}

interface WeekSchedule {
  weekNumber: number;
  focusDomain: string;
  worksheets: ScheduledWorksheet[];
  totalTime: number;
}

const domainColors: Record<string, string> = {
  'Pre-Algebra': 'bg-blue-100 text-blue-700 border-blue-200',
  'Elementary Algebra': 'bg-green-100 text-green-700 border-green-200',
  'Intermediate Algebra': 'bg-purple-100 text-purple-700 border-purple-200',
  'Coordinate Geometry': 'bg-orange-100 text-orange-700 border-orange-200',
  'Plane Geometry': 'bg-pink-100 text-pink-700 border-pink-200',
  'Trigonometry': 'bg-teal-100 text-teal-700 border-teal-200',
};

export default function ACTMathStudyPlanPage() {
  const [selectedWeeks, setSelectedWeeks] = useState<WeekOption>(8);

  // Filter out full review packs for the schedule
  const topicDrills = useMemo(() => 
    actMathWorksheets.filter(w => !w.title.includes('Full Review')),
    []
  );

  // Generate study plan based on selected weeks
  const studyPlan = useMemo(() => {
    const plan: WeekSchedule[] = [];
    const worksheetsPerWeek = Math.ceil(topicDrills.length / selectedWeeks);
    
    // Group worksheets by domain
    const domainGroups: Record<string, ScheduledWorksheet[]> = {};
    topicDrills.forEach(w => {
      if (!domainGroups[w.domain]) {
        domainGroups[w.domain] = [];
      }
      domainGroups[w.domain].push({
        id: w.id,
        title: w.title,
        domain: w.domain,
        pages: w.pages,
        difficulty: w.difficulty,
        estimatedTime: w.pages * 10,
      });
    });

    const domains = Object.keys(domainGroups);
    let currentDomainIndex = 0;
    let worksheetIndex = 0;

    for (let week = 1; week <= selectedWeeks; week++) {
      const currentDomain = domains[currentDomainIndex % domains.length];
      const weekWorksheets: ScheduledWorksheet[] = [];
      
      // Distribute worksheets across weeks
      const worksheetsThisWeek = Math.min(
        worksheetsPerWeek,
        topicDrills.length - worksheetIndex
      );

      for (let i = 0; i < worksheetsThisWeek && worksheetIndex < topicDrills.length; i++) {
        const worksheet = topicDrills[worksheetIndex];
        weekWorksheets.push({
          id: worksheet.id,
          title: worksheet.title,
          domain: worksheet.domain,
          pages: worksheet.pages,
          difficulty: worksheet.difficulty,
          estimatedTime: worksheet.pages * 10,
        });
        worksheetIndex++;
      }

      const totalTime = weekWorksheets.reduce((sum, w) => sum + w.estimatedTime, 0);

      plan.push({
        weekNumber: week,
        focusDomain: currentDomain,
        worksheets: weekWorksheets,
        totalTime,
      });

      currentDomainIndex++;
    }

    return plan;
  }, [selectedWeeks, topicDrills]);

  const totalScheduled = studyPlan.reduce((sum, week) => sum + week.worksheets.length, 0);
  const totalAvailable = topicDrills.length;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-pink-600 text-white py-16 print:py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <i className="ri-calendar-schedule-line text-4xl"></i>
            <h1 className="text-4xl font-bold">ACT Math Study Plan</h1>
          </div>
          <p className="text-xl text-white/90 max-w-3xl">
            Your personalized weekly prep schedule with all 60 topic-specific drills mapped to a structured timeline leading up to test day
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Controls */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 print:hidden">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                How many weeks until test day?
              </label>
              <div className="flex gap-3">
                {[4, 6, 8, 12].map((weeks) => (
                  <button
                    key={weeks}
                    onClick={() => setSelectedWeeks(weeks as WeekOption)}
                    className={`px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap cursor-pointer ${
                      selectedWeeks === weeks
                        ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {weeks} Weeks
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-600">Total Worksheets</div>
                <div className="text-2xl font-bold text-violet-600">
                  {totalScheduled} / {totalAvailable}
                </div>
              </div>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-printer-line text-xl"></i>
                Print Study Plan
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Study Plan Coverage</span>
              <span className="text-sm font-semibold text-violet-600">
                {Math.round((totalScheduled / totalAvailable) * 100)}%
              </span>
            </div>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-violet-600 to-purple-600 transition-all duration-500"
                style={{ width: `${(totalScheduled / totalAvailable) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <a
            href="/test-prep/act/math"
            className="block bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-shadow group"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
              <i className="ri-book-open-line text-2xl text-blue-600"></i>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">ACT Math Curriculum</h3>
            <p className="text-sm text-slate-600">Review all topics and practice questions</p>
          </a>

          <a
            href="/act-math-worksheets"
            className="block bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-shadow group"
          >
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
              <i className="ri-file-list-3-line text-2xl text-green-600"></i>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Browse Worksheets</h3>
            <p className="text-sm text-slate-600">Filter and download topic-specific drills</p>
          </a>

          <a
            href="/act-math-progress"
            className="block bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-shadow group"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
              <i className="ri-checkbox-circle-line text-2xl text-purple-600"></i>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Track Progress</h3>
            <p className="text-sm text-slate-600">Mark worksheets complete and see stats</p>
          </a>
        </div>

        {/* Weekly Schedule */}
        <div className="space-y-6">
          {studyPlan.map((week) => (
            <div
              key={week.weekNumber}
              className="bg-white rounded-2xl shadow-lg overflow-hidden print:break-inside-avoid print:shadow-none print:border print:border-gray-300"
            >
              {/* Week Header */}
              <div className="bg-gradient-to-r from-violet-600 to-purple-600 text-white px-8 py-6 print:py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold mb-1 print:text-xl">
                      Week {week.weekNumber}
                    </h2>
                    <p className="text-white/90">
                      {week.worksheets.length} worksheets • ~{Math.round(week.totalTime)} minutes total
                    </p>
                  </div>
                  <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg">
                    <i className="ri-focus-line text-xl"></i>
                    <span className="font-semibold">Mixed Topics</span>
                  </div>
                </div>
              </div>

              {/* Worksheets List */}
              <div className="p-8 print:p-4">
                <div className="space-y-4">
                  {week.worksheets.map((worksheet, index) => (
                    <div
                      key={worksheet.id}
                      className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors print:bg-white print:border print:border-gray-200"
                    >
                      {/* Day Number */}
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-violet-600 to-purple-600 text-white rounded-xl flex items-center justify-center font-bold text-lg print:w-10 print:h-10 print:text-base">
                        {index + 1}
                      </div>

                      {/* Worksheet Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 mb-1 print:text-sm">
                          {worksheet.title}
                        </h3>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium border ${
                              domainColors[worksheet.domain]
                            }`}
                          >
                            {worksheet.domain}
                          </span>
                          <span className="text-sm text-gray-600">
                            {worksheet.pages} pages
                          </span>
                          <span className="text-sm text-gray-600">
                            ~{worksheet.estimatedTime} min
                          </span>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              worksheet.difficulty === 'Easy'
                                ? 'bg-green-100 text-green-700'
                                : worksheet.difficulty === 'Medium'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {worksheet.difficulty}
                          </span>
                        </div>
                      </div>

                      {/* Download Button */}
                      <button className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 transition-colors whitespace-nowrap cursor-pointer print:hidden">
                        <i className="ri-download-line"></i>
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Study Tips */}
        <div className="mt-8 bg-gradient-to-r from-violet-100 to-purple-100 rounded-2xl p-8 print:break-inside-avoid">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-violet-600 text-white rounded-xl flex items-center justify-center">
              <i className="ri-lightbulb-line text-2xl"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Study Tips</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-violet-600 mt-1"></i>
                  <span>Complete worksheets in order for optimal skill progression</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-violet-600 mt-1"></i>
                  <span>Review incorrect answers immediately and redo similar problems</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-violet-600 mt-1"></i>
                  <span>Take a full-length practice test every 2-3 weeks to track progress</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-violet-600 mt-1"></i>
                  <span>Focus extra time on domains where you score below 70%</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:break-inside-avoid {
            break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
}